/**
 * S1 (AB#2204, SG-11) — SignalR reconnect policy that gives up when the server
 * refuses the session (401 / 403: logged out, expired, or a staff member whose
 * salon access was removed) or when there is no access token left to present.
 * Without it a revoked session kept retrying the hub negotiate in a loop.
 *
 * Otherwise it keeps the SignalR default backoff (0 / 2 / 10 / 30 s, then stop).
 */
import type { IRetryPolicy, RetryContext } from '@microsoft/signalr';

const DEFAULT_DELAYS_MS = [0, 2000, 10000, 30000];

/** True when a hub error means "this session is not allowed" (HTTP 401 / 403). */
export function isAuthRejection(error: unknown): boolean {
    if (!error) return false;
    const statusCode = (error as { statusCode?: unknown }).statusCode;
    if (statusCode === 401 || statusCode === 403) return true;
    const message = error instanceof Error ? error.message : String(error);
    // Negotiate failures surface as "Failed to complete negotiation with the
    // server: Error: …: Status code '401'".
    return /status code '?(401|403)'?/i.test(message) || /\bunauthori[sz]ed\b|\bforbidden\b/i.test(message);
}

function hasAccessToken(): boolean {
    try {
        return !!localStorage.getItem('accessToken');
    } catch {
        return false;
    }
}

export const authAwareRetryPolicy: IRetryPolicy = {
    nextRetryDelayInMilliseconds(context: RetryContext): number | null {
        if (!hasAccessToken()) return null;
        if (isAuthRejection(context.retryReason)) return null;
        return DEFAULT_DELAYS_MS[context.previousRetryCount] ?? null;
    },
};
